import { DriveStep } from 'driver.js';
import { TOUR_DESCRIPTIONS } from './tourDescriptions';

interface User {
  name: string;
  displayRole: string;
  menus: string[];
  role?: string;
}

const ensureUserMenuOpen = () => {
  if (!document.getElementById('tour-header-profile')) {
    document.getElementById('tour-header-welcome')?.click();
  }
};

export const buildAuthorSteps = (user: User): DriveStep[] => {
  const steps: DriveStep[] = [
    {
      element: '#tour-header-welcome',
      popover: {
        title: 'Welcome to TETFund TAG 👋',
        description: `Hi ${user.name.trim() || 'User'}! This is your portal for submitting and tracking manuscripts. Let's take a quick tour.`,
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '#tour-sidebar',
      popover: {
        title: 'Your Navigation',
        description: 'This sidebar contains all the features available to you.',
        side: 'right',
      },
    },
  ];

  // We add steps dynamically based on what author-menus are present/assigned
  // Common author menus:
  const authorMenuDetails = [
    { id: 'author-dashboard', title: 'Dashboard', desc: 'Your personal dashboard. See a summary of your submissions, their current status, and recent activity at a glance.' },
    { id: 'create-manuscript', title: 'Submission of Manuscript', desc: 'Submit a new manuscript for review. Fill in the manuscript details, upload your document, and track it through the review process.' },
    { id: 'submission-journal', title: 'Submission of Journal', desc: 'Apply for TETFund Journal Support. Complete the application form and upload the required journal documentation.' },
    { id: 'my-submissions', title: 'My Submissions', desc: 'View all your past and current submissions in one place. Track their review status, download feedback, and respond to revision requests.' },
    { id: 'library', title: 'Library', desc: 'Access the TETFund resource library. Browse published journals, guidelines, and reference materials available to all users.' },
  ];

  authorMenuDetails.forEach((menu) => {
    // If user has access to this menu, add it to the tour
    if (menu.id === 'library' || (user.menus && user.menus.includes(menu.id))) {
      steps.push({
        element: `#tour-nav-${menu.id}`,
        popover: {
          title: menu.title,
          description: menu.desc,
          side: 'right',
        },
      });
    }
  });

  // Notifications Step
  steps.push({
    element: '#tour-header-bell',
    popover: {
      title: 'Notifications',
      description: 'Your notification centre. Stay updated on submission status changes, review assignments, and messages from the system.',
      side: 'bottom',
    },
  });

  // Guide Button Step
  steps.push({
    element: '#tour-header-guide',
    popover: {
      title: 'Need Help Later?',
      description: 'Click this button any time to replay this tour guide.',
      side: 'bottom',
      onNextClick: (_el, _step, { driver: d }) => {
        ensureUserMenuOpen();
        // Give React a tick to render the dropdown contents before driver advances.
        setTimeout(() => d.moveNext(), 60);
      },
    },
  });

  // User menu items inside the avatar dropdown
  const profileKey = user.role === 'accessor' ? 'profile-reviewer' : 'profile';
  steps.push({
    element: '#tour-header-profile',
    popover: {
      title: 'View Profile',
      description: TOUR_DESCRIPTIONS[profileKey],
      side: 'left',
    },
    onHighlightStarted: () => ensureUserMenuOpen(),
  });

  steps.push({
    element: '#tour-header-change-password',
    popover: {
      title: 'Change Password',
      description: TOUR_DESCRIPTIONS['change-password'],
      side: 'left',
    },
    onHighlightStarted: () => ensureUserMenuOpen(),
  });

  steps.push({
    element: '#tour-header-signout',
    popover: {
      title: 'Sign Out',
      description: TOUR_DESCRIPTIONS['sign-out'],
      side: 'left',
    },
    onHighlightStarted: () => ensureUserMenuOpen(),
  });

  return steps;
};
